import {useMemo, useState, useEffect} from 'react'
import {useTable, usePagination} from 'react-table'
import {useIntl, FormattedMessage} from 'react-intl'
import {KTIcon} from '../../../_metronic/helpers'
import {useParams, Link} from 'react-router-dom'
import {FindPodIdByCode, CheckPodAccess, FindFirstPodCode} from '../FindPodIdByPodCode'
import usePostApi from '../custom_hooks/usePostApi'
import {defaultProfileImage} from '../core'
import {PageTitle} from '../../../_metronic/layout/core'
import TeamMembersKnowMore from '../know_more_modals/TeamMembersKmMain'
import TeamMembersKM from '../know_more_modals/team_members_KM/TeamMembers'
import 'react-datepicker/dist/react-datepicker.css'
import DateFormatter from '../DateFormatter'
import PropTypes from 'prop-types'
import {Pagination} from 'react-bootstrap'
import {UseToast} from '../useToast'
import {LightOutlineButton} from '../../ui/LightOutlineButton'
import {PrimaryOutlineButton} from '../../ui/PrimaryOutlineButton'
import {useThemeMode} from '../../../_metronic/partials'
import secureLocalStorage from 'react-secure-storage'
import CryptoJS from 'crypto-js'

const secretKey = process.env.REACT_APP_REQUEST_SIGN_SECRET_KEY

const DataTable = ({columns, data, isLoading, fetchData, refresh}) => {
  const havePodAccess = CheckPodAccess()
  const firstPodCode = FindFirstPodCode()
  const [showKM, setShowKM] = useState(false)
  const intl = useIntl()
  const {id} = useParams()
  const documents = [
    {
      title: `(${id})`,
      path: `/${id}/documents`,
      isSeparator: false,
      isActive: false,
    },
  ]

  const [inputVal, setInputVal] = useState('')
  const [currentStatus, setCurrentStatus] = useState('pending')

  const filteredData = useMemo(() => {
    return data
      ? data.filter((each) => {
          const docTitle = each?.doc_title
          const status = each?.doc_status?.toString()

          const docTitleMatches = docTitle?.toLowerCase()?.includes(inputVal?.toLowerCase())

          let statusMatches = false
          if (currentStatus === 'pending') {
            statusMatches = ['S', 'IP', 'R', 'A'].includes(status)
          } else if (currentStatus === 'completed') {
            statusMatches = status === 'C'
          } else if (currentStatus === 'rejected') {
            statusMatches = status === 'X'
          } else {
            statusMatches = true
          }

          // return docTitleMatches && roleIDMatches && statusMatches
          return docTitleMatches && statusMatches
        })
      : []
  }, [inputVal, data, currentStatus])

  const tableInstance = useTable({columns, data: filteredData}, usePagination)
  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    page,
    state: {pageIndex, pageSize},
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageCount,
    gotoPage,
    setPageSize,
  } = tableInstance

  const pageOptions = [10, 20, 30, 40, 50]

  const handleDifferentViews = () => {
    if (isLoading && refresh === false) {
      return (
        <div className='card h-250px'>
          <div className='m-auto d-flex flex-column align-items-center'>
            <div className='spinner-border spinner-primary mr-15'></div>
          </div>
        </div>
      )
    } else if (data && data.length > 0) {
      return (
        <div>
          {refresh ? (
            <div className='card h-250px'>
              <div className='m-auto d-flex flex-column align-items-center'>
                <div className='spinner-border spinner-primary mr-15'></div>
              </div>
            </div>
          ) : (
            <>
              <div className='card p-5 mb-5'>
                <div className='d-flex flex-column flex-md-row justify-content-md-between border-0 px-0 mb-5'>
                  <ul className='nav d-flex align-items-center'>
                    <li className='nav-item'>
                      <span
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-normal px-4 me-1 ${
                          currentStatus === 'pending' ? 'active' : ''
                        }`}
                        data-bs-toggle='tab'
                        onClick={() => setCurrentStatus('pending')}
                      >
                        In Progress
                      </span>
                    </li>
                    <li className='nav-item'>
                      <span
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-success fw-normal px-4 me-1 ${
                          currentStatus === 'completed' ? 'active' : ''
                        }`}
                        data-bs-toggle='tab'
                        onClick={() => setCurrentStatus('completed')}
                      >
                        Complete
                      </span>
                    </li>
                    <li className='nav-item'>
                      <span
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-danger fw-normal px-4 me-1 ${
                          currentStatus === 'rejected' ? 'active' : ''
                        }`}
                        data-bs-toggle='tab'
                        onClick={() => setCurrentStatus('rejected')}
                      >
                        Rejected
                      </span>
                    </li>
                  </ul>

                  <div className='d-flex flex-wrap gap-3'>
                    <div className='d-flex align-items-center position-relative'>
                      <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-3' />
                      <input
                        type='search'
                        id='kt_filter_search'
                        className='form-control form-control-white form-control-sm w-150px ps-9 fw-normal'
                        placeholder={intl.formatMessage({id: 'LABEL.SEARCH'})}
                        value={inputVal}
                        onChange={(e) => setInputVal(e.target.value)}
                        autoComplete='off'
                      />
                    </div>

                    {/* <button
                      className='btn btn-sm btn-light-primary fw-normal'
                      onClick={() => setAddModal(true)}
                    >
                      <KTIcon iconName='plus' className='fs-3' />
                      New Member
                    </button> */}

                    <LightOutlineButton
                      className='btn btn-sm btn-bg-light fw-normal px-3 pe-2 btn-active-primary'
                      onClick={() => fetchData(true)}
                    >
                      <i className='ki-solid ki-arrows-circle fs-3'></i>
                    </LightOutlineButton>
                  </div>
                </div>

                <div className='mb-0'>
                  <div className='overflow-auto'>
                    <table
                      className='table table-row-dashed table-row-gray-300 align-middle'
                      {...getTableProps()}
                    >
                      <thead>
                        <tr className='fw-semibold text-muted bg-light rounded'>
                          <th className='min-w-100px ps-3'>Document Title</th>
                          <th className='min-w-100px'>Status</th>
                          <th className='min-w-150px'>Date Sent</th>
                          <th className='min-w-150px'>Expiry</th>
                          <th className='min-w-150px text-center pe-3'>Actions</th>
                        </tr>
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {page.length > 0 ? (
                          page.map((row) => {
                            prepareRow(row)
                            return (
                              <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => (
                                  <td className='text-center' {...cell.getCellProps()}>
                                    {cell.render('Cell')}
                                  </td>
                                ))}
                              </tr>
                            )
                          })
                        ) : (
                          <tr>
                            <td className='' colSpan={5}>
                              <div className='d-flex justify-content-center'>
                                <p className='text-dark default-cursor d-block fs-6 m-0 my-5 ps-3'>
                                  {inputVal === '' ? (
                                    intl.formatMessage({id: 'NO_RESULTS_FOUND_MSG'})
                                  ) : (
                                    <FormattedMessage
                                      id='MSG.NO_DATA_MATCH'
                                      values={{
                                        inputVal: <span className='text-primary'>{inputVal}</span>,
                                      }}
                                    />
                                  )}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {filteredData.length > 10 && (
                <Pagination>
                  <Pagination.First onClick={() => gotoPage(0)} disabled={!canPreviousPage} />
                  <Pagination.Prev onClick={previousPage} disabled={!canPreviousPage} />

                  {[...Array(pageCount)].map((_, i) => (
                    <Pagination.Item key={i} active={i === pageIndex} onClick={() => gotoPage(i)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}

                  <Pagination.Next onClick={nextPage} disabled={!canNextPage} />
                  <Pagination.Last
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                  />

                  <div className='d-flex align-items-center mx-5'>
                    <span className='me-2'>{intl.formatMessage({id: 'BTN.SHOW'})}</span>
                    <select
                      className='form-select form-select-solid fw-bold w-75px me-2'
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {pageOptions.map((option) => (
                        <option key={option} value={option} className='select-bg'>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </Pagination>
              )}
            </>
          )}
        </div>
      )
    } else {
      return (
        <>
          <div className='card text-center h-100 d-flex flex-column justify-content-center p-10'>
            <h4 className='fw-semibold mb-3'>No Sent Requests</h4>
            <p className='text-muted mb-4'>
              You haven’t sent any document requests yet. Start by sending a request to begin the
              signing process.
            </p>
          </div>

          {/* <h3 className='mb-4 fs-3 fw-semibold'>Stay on top of your Documents</h3>

          <p>
            This is your central hub for managing all your important documents. Whether you're
            sending documents out for signature or waiting to review and sign, you'll find their
            status here.
          </p>

          <div className='text-center mb-4'>
            <TeamMembersKM />
          </div> */}
        </>
      )
    }
  }

  return havePodAccess ? (
    <>
      <TeamMembersKnowMore showModal={showKM} setShowModal={setShowKM} />

      <PageTitle breadcrumbs={documents}>Documents</PageTitle>
      {handleDifferentViews()}
    </>
  ) : (
    <div className='card min-h-250px d-flex flex-column justify-content-center align-items-center bg-image'>
      <h1 className='fw-semibold fs-2hx text-gray-900 mb-4'>Oops!</h1>
      <div className='fs-6 text-gray-500 mb-7'>You don't have access to this POD.</div>
      <div className='mb-0'>
        <Link to={`/${firstPodCode}/dashboard`} className='btn btn-sm btn-light-primary'>
          Return Home
        </Link>
      </div>
    </div>
  )
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      Cell: PropTypes.func.isRequired,
      disableSortBy: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fetchData: PropTypes.func.isRequired,
}

const DocsMain = () => {
  const {id} = useParams()
  const {mode} = useThemeMode()
  const podId = FindPodIdByCode()
  const {execute, loading} = usePostApi()
  const [teamMembersData, setTeamMembersData] = useState([])
  const [refresh, setRefresh] = useState(false)

  const getDocuments = async (show) => {
    if (show) {
      setRefresh(true)
    }
    try {
      const url = '/document/request/get'
      const formData = {
        pod_id: podId,
      }

      const response = await execute(url, 'POST', formData)
      setTeamMembersData(response.data)
      setRefresh(false)
      if (show) {
        return UseToast(
          response.status_message,
          {
            position: 'bottom-right',
            theme: 'colored',
            autoClose: true,
          },
          'success'
        )
      }
    } catch (error) {
      console.error('Error:', error)
      setRefresh(false)
      if (show) {
        return UseToast(
          error.response?.data?.status_message,
          {
            position: 'bottom-right',
            theme: 'colored',
            autoClose: true,
          },
          'error'
        )
      }
    }
  }

  useEffect(() => {
    if (podId) {
      getDocuments()
    }
  }, [podId])

  const getStatusName = (status) => {
    switch (status) {
      case 'S':
        return 'Submitted'
      case 'X':
        return 'Cancelled '
      case 'A':
        return 'Approved'
      case 'IP':
        return 'In Progress'
      case 'C':
        return 'Completed'
      case 'R':
        return 'Reserved'
      default:
        return status
    }
  }

  const userInfo = secureLocalStorage.getItem('user_info')
  let user_id = ''
  let auth_token = ''
  if (userInfo) {
    ;({user_id, auth_token} = userInfo)
  }

  const payload = {
    pod_id: podId,
    user_id,
    auth_token,
    pod_code: id,
  }

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), secretKey).toString()
  const encoded = encodeURIComponent(encrypted)

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({row}) => {
          return (
            <div className='d-flex align-items-center ps-3'>
              {/* <div className='symbol symbol-50px me-5'>
                <img src={row.original.profile_pic || defaultProfileImage} alt='' />
              </div> */}
              <div className='d-flex justify-content-start flex-column text-start'>
                <p className='text-dark fw-semibold text-hover-primary mb-1 fs-6 default-cursor'>
                  {row.original.doc_title}
                </p>
                <span className='fs-8 fw-semibold text-muted'>{row.original.doc_uniq_id}</span>
              </div>
            </div>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({row}) => {
          const {doc_status} = row.original
          const statusClass =
            {
              S: 'badge badge-light-primary',
              X: 'badge badge-light-primary',
              R: 'badge badge-light-primary',
              A: 'badge badge-light-success',
              C: 'badge badge-light-success',
              IP: 'badge badge-light-info',
            }[doc_status] || 'badge badge-light-danger'

          return (
            <div className='text-start'>
              <span className={statusClass + ' fw-normal'}>{getStatusName(doc_status)}</span>
            </div>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'CreatedAt',
        accessor: 'created_at',
        Cell: ({row}) => {
          return (
            <div className='d-flex align-items-center'>
              <div className='d-flex justify-content-start flex-column text-start'>
                <p className='text-dark fw-normal text-hover-primary mb-1 fs-6 default-cursor'>
                  {row.original.created_ts ? DateFormatter(row.original.created_ts) : ''}
                  {row.original.created_ts && ', '}

                  {row.original.created_ts &&
                    row.original.created_ts.length >= 16 &&
                    row.original.created_ts.slice(11, 16)}
                </p>
              </div>
            </div>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Expiry',
        accessor: 'Expiry',
        Cell: ({row}) => {
          const {expiry_ts} = row.original
          return (
            <div className='d-flex align-items-center'>
              <div className='d-flex justify-content-start flex-column text-start'>
                <p className='text-dark fw-normal text-hover-primary mb-1 fs-6 default-cursor'>
                  {expiry_ts ? DateFormatter(expiry_ts) : ''}
                  {expiry_ts && ', '}

                  {expiry_ts && expiry_ts.length >= 16 && expiry_ts.slice(11, 16)}
                  {!expiry_ts && '-'}
                </p>
              </div>
            </div>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({row}) => {
          const {doc_status, request_id} = row.original
          const encodedRequestId = btoa(request_id.toString())
          const url = `${process.env.REACT_APP_REQUEST_SIGN_PATH}/request-signatures/${encodedRequestId}/edit?data=${encoded}`

          return (
            <div className='text-center'>
              {doc_status === 'S' ? (
                <a
                  href={url}
                  className={`btn btn-sm bg-${
                    mode === 'dark' ? 'secondary' : 'white'
                  } btn-outline border-primary text-primary btn-active-light-primary d-inline-flex align-items-center`}
                >
                  <KTIcon iconName='pencil' className='fs-3 text-primary' />
                  Edit
                </a>
              ) : (
                <>
                  <a
                    href={row.original?.doc_metadata?.key}
                    target='_blank'
                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                    rel='noreferrer'
                    type='application/pdf'
                  >
                    <KTIcon iconName='document' className='fs-3' />
                  </a>
                  <Link
                    to={`${row.original.request_id}`}
                    className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                  >
                    <KTIcon iconName='eye' className='fs-3' />
                  </Link>
                </>
              )}
            </div>
          )
        },
      },
    ],
    []
  )

  return (
    <DataTable
      columns={columns}
      data={teamMembersData || []}
      isLoading={loading}
      fetchData={getDocuments}
      refresh={refresh}
    />
  )
}

export {DocsMain}
